import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Crown,
  ShieldCheck,
  CaretDown,
  SignOut,
  CaretUpDown,
} from '@phosphor-icons/react';
import { useAuth } from '@/features/auth/AuthContext';
import { useCompany } from '@/features/company/CompanyContext';
import { useTenant } from '@/context/tenant-context';
import { analyticsApi, type AnalyticsTenantOption } from '@/lib/analytics-api';
import { cn } from '@/lib/utils';
import { NAV_CONFIG, type NavEntry } from '@/routes/nav-config';

function SidebarNavItem({
  label,
  icon: Icon,
  isActive,
  highlight,
  onClick,
}: {
  label: string;
  icon: React.ElementType;
  isActive: boolean;
  highlight?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
        'border-l-[2px] -ml-px',
        isActive
          ? 'border-l-blue-400 bg-slate-800/60 text-slate-100'
          : highlight
            ? 'border-l-transparent text-slate-200 hover:bg-slate-800/40'
            : 'border-l-transparent text-slate-400 hover:bg-slate-800/40 hover:text-slate-200',
      )}
    >
      <Icon
        size={18}
        weight={isActive ? 'fill' : 'regular'}
        className={cn(
          'shrink-0 transition-colors',
          isActive ? 'text-slate-100' : 'text-slate-500 group-hover:text-slate-300',
        )}
      />
      <span className="truncate">{label}</span>
    </button>
  );
}

function ScopeSwitcher() {
  const { user } = useAuth();
  const { companyId, siteId, setCompanyId, setSiteId } = useCompany();
  const {
    selectedTenantId,
    setSelectedTenantId,
    selectedTenantTimezone,
    setSelectedTenantTimezone,
    isPlatformAdmin,
  } = useTenant();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: tenants } = useQuery({
    queryKey: ['analytics', 'tenants'],
    queryFn: () => analyticsApi.tenants(),
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!tenants?.length) return;
    const defaultTenantId = selectedTenantId || user?.companyId;
    if (!defaultTenantId) return;
    const tenantExists = tenants.some((tenant) => tenant.id === defaultTenantId);
    if (!tenantExists) return;

    if (selectedTenantId !== defaultTenantId) {
      setSelectedTenantId(defaultTenantId);
    }
    if (companyId !== defaultTenantId) {
      setCompanyId(defaultTenantId);
    }
  }, [companyId, selectedTenantId, setCompanyId, setSelectedTenantId, tenants, user?.companyId]);

  const currentTenantId = selectedTenantId || companyId || user?.companyId || null;
  const currentTenant = (tenants as AnalyticsTenantOption[] | undefined)?.find((tenant) => tenant.id === currentTenantId) ?? null;
  const currentSites = currentTenant?.sites ?? [];

  useEffect(() => {
    const timezone = currentTenant?.timezone ?? 'America/New_York';
    if (selectedTenantTimezone !== timezone) {
      setSelectedTenantTimezone(timezone);
    }
  }, [currentTenant?.timezone, selectedTenantTimezone, setSelectedTenantTimezone]);

  useEffect(() => {
    // Wait for the tenant list: while it loads there are no sites, and a
    // restored choice would be dropped on every reload.
    if (!siteId || !currentTenant) return;
    if (!currentSites.some((site) => site.id === siteId)) {
      setSiteId(undefined);
    }
  }, [currentTenant, currentSites, setSiteId, siteId]);

  const currentTenantName = currentTenant?.name ?? currentTenantId ?? 'Select tenant';
  const currentSiteName =
    currentSites.find((site) => site.id === siteId)?.name ??
    (currentSites.length > 0 ? 'All sites' : 'Tenant rollup');

  return (
    <div className="flex items-center gap-2">
      <div ref={ref} className="relative">
        <button
          onClick={() => {
            if (!isPlatformAdmin) return;
            setOpen((value) => !value);
          }}
          className={cn(
            'flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
            'border-slate-200 bg-white hover:border-slate-300',
            open && 'border-slate-300 ring-1 ring-slate-200',
            !isPlatformAdmin && 'cursor-default',
          )}
        >
          <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-slate-900 font-mono text-[10px] uppercase text-white">
            {currentTenantName?.charAt(0) || '?'}
          </span>
          <span className="max-w-[180px] truncate font-medium text-slate-700">{currentTenantName}</span>
          {isPlatformAdmin ? <CaretUpDown size={14} className="shrink-0 text-slate-400" /> : null}
        </button>

        {open && isPlatformAdmin && (
          <div className="absolute right-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
            <div className="p-1.5">
              <p className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Switch Tenant
              </p>
              {(tenants ?? []).map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => {
                    setSelectedTenantId(tenant.id);
                    setCompanyId(tenant.id);
                    setSiteId(undefined);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors',
                    tenant.id === currentTenantId
                      ? 'bg-slate-100 font-medium text-slate-900'
                      : 'text-slate-600 hover:bg-slate-50',
                  )}
                >
                  <span className={cn(
                    'inline-flex h-5 w-5 items-center justify-center rounded font-mono text-[10px] uppercase',
                    tenant.id === currentTenantId ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500',
                  )}>
                    {tenant.name.charAt(0)}
                  </span>
                  <span className="truncate">{tenant.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {currentSites.length > 0 ? (
        <label className="flex items-center gap-2">
          <span className="sr-only">Select site scope</span>
          <select
            aria-label="Select site scope"
            value={siteId ?? ''}
            onChange={(event) => setSiteId(event.target.value || undefined)}
            className="max-w-[220px] rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-all duration-150 hover:border-slate-300 hover:shadow-sm"
          >
            <option value="">All sites (rollup)</option>
            {currentSites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-500">
          {currentSiteName}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 font-mono text-[11px] uppercase text-white">
          {user?.email?.charAt(0) || '?'}
        </span>
        <span className="hidden max-w-[120px] truncate font-medium text-slate-700 sm:block">{user?.email || 'User'}</span>
        <CaretDown size={12} weight="bold" className="text-slate-400" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-3 py-2.5">
            <p className="truncate text-[13px] font-medium text-slate-800">{user?.displayName || user?.email}</p>
            <p className="truncate font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">{user?.tokenRole || user?.role || 'user'}</p>
          </div>
          <div className="p-1.5">
            <button
              onClick={() => { logout(); setOpen(false); }}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-rose-700 transition-colors hover:bg-rose-50"
            >
              <SignOut size={16} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isPlatformAdmin } = useTenant();

  const analyticsRole = user?.tokenRole ?? user?.role ?? 'tenant_user';

  const isActive = (path: string, exact?: boolean) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/cases') {
      return (
        location.pathname === '/cases' ||
        (/^\/cases\/[^/]+$/.test(location.pathname) && location.pathname !== '/cases/review')
      );
    }
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const canViewEntry = (entry: NavEntry) => {
    if (entry.type !== 'item' || !entry.roles) return true;
    if (entry.roles.includes(analyticsRole)) return true;
    if (isPlatformAdmin && (entry.roles.includes('super_admin') || entry.roles.includes('kombatix_qa'))) {
      return true;
    }
    return false;
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-slate-800/60 bg-slate-950">
        <div className="relative flex h-20 items-center justify-center border-b border-slate-800/60 px-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-700 bg-slate-900">
              <ShieldCheck size={18} weight="regular" className="text-slate-100" />
            </div>
            <div>
              <span className="text-[15px] font-semibold tracking-tight text-slate-100">Intellitryx</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-blue-400">Defense Cockpit</span>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5">
          <div className="flex min-h-full flex-col">
            {NAV_CONFIG.map((entry, index) => {
              if (!canViewEntry(entry)) return null;

              if (entry.type === 'section_label') {
                return (
                  <p key={`${entry.label}-${index}`} className="mt-6 mb-1 px-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {entry.label}
                  </p>
                );
              }

              if (entry.type === 'spacer') {
                return <div key={`spacer-${index}`} className="flex-1" />;
              }

              return (
                <div key={entry.path} className="mb-0.5">
                  <SidebarNavItem
                    label={entry.label}
                    icon={entry.icon}
                    isActive={isActive(entry.path, entry.exact)}
                    highlight={entry.highlight}
                    onClick={() => navigate(entry.path)}
                  />
                </div>
              );
            })}

            {/* Kombatix staff ONLY. Gated on `isPlatformAdmin` (tokenRole
                super_admin / kombatix_qa), NEVER on the legacy `user.role`
                field: `user_mappings/darrel@peoplefinders.com` holds
                `role: "admin"` for a TENANT admin, so `user?.role === 'admin'`
                rendered this crown for a PeopleFinders user and showed them the
                tenant roster and our GCP topology. Same trap tenant-context.tsx
                documents. The route itself is gated too — hiding the link never
                stopped anyone typing /admin. */}
            {isPlatformAdmin ? (
              <div className="mt-4">
                <p className="mt-6 mb-1 px-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Admin</p>
                <SidebarNavItem
                  label="Admin Portal"
                  icon={Crown}
                  isActive={isActive('/admin')}
                  onClick={() => navigate('/admin')}
                />
              </div>
            ) : null}
          </div>
        </nav>
      </aside>

      <div className="flex flex-1 flex-col pl-64 min-w-0">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-end gap-4 border-b border-slate-200/80 bg-white/80 px-6 backdrop-blur-sm">
          <ScopeSwitcher />
          <div className="h-5 w-px bg-slate-200" />
          <UserMenu />
        </header>

        <main className="flex-1 min-w-0 p-4 lg:p-8">
          <div className="mx-auto max-w-[1600px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
